import { createPdfHelpers, PdfHelpers, PDF_MARGIN_MM, PDF_PAGE_WIDTH_MM, PDF_CONTENT_WIDTH_MM } from './gestion-tecnica-documentos'
import { PsicosocialAssessment, PsicosocialResultRow, LevelCounts } from './psicosocial-persistencia'
import { PSICOSOCIAL_DIMENSIONS, PSICOSOCIAL_INSTRUMENT, PSICOSOCIAL_GLOBAL_CUTOFFS, RiskCutoffs } from './psicosocial-instrumento'
import { plural } from './plurales'

// Genera el informe PDF de resultados agregados de una evaluación
// psicosocial cerrada, siguiendo el patrón de generateNioshReportPDF():
// createPdfHelpers() para el flujo de texto, secciones con hline(), nota
// final en cursiva. Paginación "Página X de Y" con un segundo pase al
// final, igual que RISSOPage.tsx.
//
// A diferencia de los reportes de metodologías, NO se usa
// uploadAndRegisterPdf(): el informe solo se descarga localmente con
// doc.save() — no se sube a ningún bucket (uploadAndRegisterPdf registra
// en sso_documents una URL de getPublicUrl() sobre sso-documents, no apta
// para resultados psicosociales).
//
// Solo usa datos ya agregados de psychosocial_results (validados por
// mapResult): no hay respuestas individuales ni códigos de acceso.
//
// Sirve como evidencia "informe_resultados" de la actividad 3 del
// programa (psicosocial-programa.ts) y lleva metodología, resultados y
// firmas como pide GT-06 (compliance-engine.ts).

const M = PDF_MARGIN_MM
const PW = PDF_PAGE_WIDTH_MM
const CW = PDF_CONTENT_WIDTH_MM
const PH = 297

const ROW_H = 6
// Mismo límite inferior que usa checkBreak() dentro de createPdfHelpers()
// (BOTTOM = PH - 18), que no se exporta.
const BOTTOM = PH - 18
const HEADER_FILL: [number, number, number] = [30, 41, 59]
const BAR_EMPTY: [number, number, number] = [226, 232, 240]

// Mismos colores que NIVEL_COLORES_PSICO de PsicosocialResultadosPage.tsx.
const NIVELES: (keyof LevelCounts)[] = ['bajo', 'medio', 'alto']
const NIVEL_RGB: Record<keyof LevelCounts, [number, number, number]> = {
  bajo: [47, 191, 113],
  medio: [245, 158, 11],
  alto: [239, 68, 68],
}

function pct(n: number, total: number): number {
  return total === 0 ? 0 : Math.round((n / total) * 100)
}

function formatEcuadorDateTime(iso: string | null): string {
  if (iso === null) return '—'
  return new Date(iso).toLocaleString('es-EC', {
    timeZone: 'America/Guayaquil', day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function sumLevelCounts(list: LevelCounts[]): LevelCounts {
  return list.reduce(
    (acc, c) => ({ bajo: acc.bajo + c.bajo, medio: acc.medio + c.medio, alto: acc.alto + c.alto }),
    { bajo: 0, medio: 0, alto: 0 }
  )
}

function rangeText(cutoffs: RiskCutoffs, level: keyof LevelCounts): string {
  return `${cutoffs[level].min}–${cutoffs[level].max}`
}

// Barra horizontal apilada de 100% dibujada con rect(): un segmento por
// nivel, ancho proporcional al conteo. Sin datos, queda la barra gris.
function drawStackedBar(h: PdfHelpers, x: number, y: number, w: number, barH: number, counts: LevelCounts): void {
  const { doc } = h
  doc.setFillColor(...BAR_EMPTY)
  doc.rect(x, y, w, barH, 'F')
  const total = counts.bajo + counts.medio + counts.alto
  if (total === 0) return
  let cx = x
  for (const n of NIVELES) {
    if (counts[n] === 0) continue
    const segW = (counts[n] / total) * w
    doc.setFillColor(...NIVEL_RGB[n])
    doc.rect(cx, y, segW, barH, 'F')
    cx += segW
  }
}

// Barra de un solo nivel: segmento de color proporcional a value/total
// sobre fondo gris (para las filas de la tabla de resultados globales).
function drawProportionBar(h: PdfHelpers, x: number, y: number, w: number, barH: number, level: keyof LevelCounts, value: number, total: number): void {
  const { doc } = h
  doc.setFillColor(...BAR_EMPTY)
  doc.rect(x, y, w, barH, 'F')
  if (total === 0 || value === 0) return
  doc.setFillColor(...NIVEL_RGB[level])
  doc.rect(x, y, (value / total) * w, barH, 'F')
}

type BarSpec =
  | { kind: 'stacked'; counts: LevelCounts }
  | { kind: 'single'; level: keyof LevelCounts; value: number; total: number }

// Recorta el texto al ancho de la celda (con "…") para que un nombre de
// unidad largo no se salga de la tabla.
function fitText(h: PdfHelpers, text: string, maxW: number): string {
  if (h.doc.getTextWidth(text) <= maxW) return text
  let t = text
  while (t.length > 1 && h.doc.getTextWidth(t + '…') > maxW) t = t.slice(0, -1)
  return t + '…'
}

// Tabla simple con encabezado oscuro (mismo estilo que drawTable() de
// createPdfHelpers). Si se pasa `bars`, la última columna se deja vacía
// y se dibuja en ella la barra de cada fila (BarSpec): apilada con los
// tres niveles, o de un solo nivel proporcional al total. Se reserva el
// alto completo con checkBreak() antes de dibujar, para que una tabla
// corta no se corte entre páginas; si una tabla larga (p. ej.
// Participación con muchas unidades) no cabe, las filas continúan en una
// página nueva con el encabezado de columnas repetido.
function drawGridTable(
  h: PdfHelpers,
  cfg: {
    title?: string
    headers: string[]
    widths: number[]
    rows: string[][]
    bars?: (BarSpec | null)[]
    boldLastRow?: boolean
  }
): void {
  const { doc } = h
  const titleH = cfg.title ? 6 : 0
  // Tabla que cabe en una página vacía: se reserva completa (no se parte).
  // Tabla más alta que una página: se reserva solo título + encabezado +
  // unas filas, y el chequeo por fila de abajo la continúa en otra página.
  const fullH = titleH + ROW_H * (cfg.rows.length + 1) + 4
  h.checkBreak(fullH <= BOTTOM - M ? fullH : titleH + ROW_H * 4 + 4)

  if (cfg.title) {
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(9)
    doc.setTextColor(0, 0, 0)
    doc.text(cfg.title, M, h.getY() + 3)
    h.skip(titleH)
  }

  let y = h.getY()
  let x = M

  // Encabezado de columnas — se dibuja al inicio y otra vez en cada
  // página nueva si la tabla continúa.
  const drawHeaderRow = () => {
    x = M
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(7.5)
    cfg.headers.forEach((head, i) => {
      doc.setFillColor(...HEADER_FILL)
      doc.rect(x, y, cfg.widths[i], ROW_H, 'F')
      doc.setTextColor(255, 255, 255)
      doc.text(head, i === 0 ? x + 2 : x + cfg.widths[i] / 2, y + ROW_H / 2 + 1, { align: i === 0 ? 'left' : 'center' })
      x += cfg.widths[i]
    })
    y += ROW_H
  }

  drawHeaderRow()

  // Filas
  cfg.rows.forEach((row, ri) => {
    if (y + ROW_H > BOTTOM) {
      doc.addPage()
      y = M
      drawHeaderRow()
    }
    const isBold = cfg.boldLastRow === true && ri === cfg.rows.length - 1
    doc.setFont('helvetica', isBold ? 'bold' : 'normal')
    doc.setFontSize(7.5)
    doc.setTextColor(0, 0, 0)
    doc.setDrawColor(180, 180, 180)
    x = M
    row.forEach((cell, ci) => {
      doc.rect(x, y, cfg.widths[ci], ROW_H, 'S')
      if (cell !== '') {
        doc.text(fitText(h, cell, cfg.widths[ci] - 4), ci === 0 ? x + 2 : x + cfg.widths[ci] / 2, y + ROW_H / 2 + 1, { align: ci === 0 ? 'left' : 'center' })
      }
      x += cfg.widths[ci]
    })
    const bar = cfg.bars?.[ri]
    if (bar) {
      const barCol = cfg.widths.length - 1
      const barX = M + cfg.widths.slice(0, barCol).reduce((a, b) => a + b, 0)
      const bx = barX + 2, by = y + 1.6, bw = cfg.widths[barCol] - 4, bh = ROW_H - 3.2
      if (bar.kind === 'stacked') drawStackedBar(h, bx, by, bw, bh, bar.counts)
      else drawProportionBar(h, bx, by, bw, bh, bar.level, bar.value, bar.total)
    }
    y += ROW_H
  })

  doc.setFont('helvetica', 'normal')
  doc.setDrawColor(0, 0, 0)
  doc.setTextColor(0, 0, 0)
  // Sincroniza la posición de los helpers con la de la tabla. Si la tabla
  // pasó a otra página, y es menor que getY() y el delta es negativo:
  // skip() solo suma, así que el resultado sigue siendo y + 4.
  h.skip(y - h.getY() + 4)
}

// Tabla de niveles: etiqueta | Bajo n (%) | Medio n (%) | Alto n (%) | barra.
const LEVEL_WIDTHS = [58, 22, 22, 22, CW - 58 - 66]

function levelRow(label: string, counts: LevelCounts): string[] {
  const total = counts.bajo + counts.medio + counts.alto
  return [label, ...NIVELES.map(n => `${counts[n]} (${pct(counts[n], total)}%)`), '']
}

function drawLevelTable(h: PdfHelpers, title: string, firstHeader: string, items: { label: string; counts: LevelCounts }[]): void {
  drawGridTable(h, {
    title,
    headers: [firstHeader, 'Bajo', 'Medio', 'Alto', 'Distribución'],
    widths: LEVEL_WIDTHS,
    rows: items.map(it => levelRow(it.label, it.counts)),
    bars: items.map(it => ({ kind: 'stacked' as const, counts: it.counts })),
  })
}

// Dos bloques de firma lado a lado (GT-06 pide firmas en el informe).
function drawSignatureBlocks(h: PdfHelpers): void {
  const { doc } = h
  h.checkBreak(50)
  const top = h.getY()
  const colW = (CW - 10) / 2
  const cols = [
    { x: M, title: 'Elaborado por' },
    { x: M + colW + 10, title: 'Revisado por' },
  ]
  for (const col of cols) {
    let y = top
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(9)
    doc.setTextColor(0, 0, 0)
    doc.text(col.title, col.x, y)
    y += 8
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(8.5)
    for (const field of ['Nombre:', 'Cargo:', 'Fecha:']) {
      doc.text(field, col.x, y)
      doc.setDrawColor(150, 150, 150)
      doc.line(col.x + 16, y + 0.5, col.x + colW, y + 0.5)
      y += 8
    }
    y += 8
    doc.line(col.x, y, col.x + colW, y)
    doc.text('Firma', col.x + colW / 2, y + 4, { align: 'center' })
  }
  doc.setDrawColor(0, 0, 0)
  h.skip(50)
}

export function buildPsicosocialResultsFilename(assessmentTitle: string): string {
  const sinTildes = assessmentTitle.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  return `Resultados_Psicosocial_${sinTildes.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`
}

export function generatePsicosocialResultsPDF(input: {
  assessment: PsicosocialAssessment
  results: PsicosocialResultRow[]
  companyName: string
  companyRuc: string | null
}): void {
  const { assessment, results, companyName, companyRuc } = input
  if (results.length === 0) {
    throw new Error('No hay resultados guardados para generar el informe')
  }

  const helpers = createPdfHelpers()
  const { txt, hline, skip, doc } = helpers

  // Cada sección reserva espacio para su título y lo primero que sigue
  // (texto o tabla), para que un título nunca quede solo al pie de página.
  const startSection = (title: string, reserveMm: number) => {
    helpers.checkBreak(reserveMm + 14)
    hline()
    txt(title, 11, 'bold', 'left', 0, 4)
  }

  const published = results.filter(r => !r.suppressed && r.distributions !== null)
  const suppressed = results.filter(r => r.suppressed || r.distributions === null)
  const minGroup = results[0].minGroup
  const otros = PSICOSOCIAL_DIMENSIONS.find(d => d.key === 'otros_puntos_importantes')
  const subdimensions = otros?.subdimensions ?? []

  // 1. Cabecera
  txt(companyName, 14, 'bold', 'center', 0, 2)
  if (companyRuc) txt(`RUC: ${companyRuc}`, 10, 'normal', 'center', 0, 2)
  txt('Informe de Resultados — Riesgo Psicosocial', 12, 'bold', 'center', 0, 4)
  txt(assessment.title, 11, 'bold', 'center', 0, 6)

  // 2. Identificación
  startSection('1. Identificación de la evaluación', 20)
  txt(`Período de aplicación: ${formatEcuadorDateTime(assessment.openedAt)} a ${formatEcuadorDateTime(assessment.closedAt)} (hora de Ecuador)`, 9, 'normal', 'left', 4, 2)
  txt(`Contacto del responsable: ${assessment.contactEmail ?? '—'} · ${assessment.contactPhone ?? '—'}`, 9, 'normal', 'left', 4, 2)
  txt(`Resultados calculados el: ${formatEcuadorDateTime(results[0].computedAt)}`, 9, 'normal', 'left', 4, 4)

  // 3. Ficha metodológica
  startSection('2. Ficha metodológica', 30)
  txt(`Instrumento: ${assessment.instrumentId} — versión ${assessment.instrumentVersion}`, 9, 'normal', 'left', 4, 2)
  txt(`Fuente: ${PSICOSOCIAL_INSTRUMENT.source}`, 9, 'normal', 'left', 4, 2)
  txt(
    `Cuestionario de ${PSICOSOCIAL_INSTRUMENT.itemCount} ítems agrupados en ${PSICOSOCIAL_DIMENSIONS.length} dimensiones, ` +
    `con escala Likert de 4 puntos (${PSICOSOCIAL_INSTRUMENT.responseScale.map(o => `${o.value} = ${o.label}`).join('; ')}).`,
    9, 'normal', 'left', 4, 2
  )
  txt(
    'Cada cuestionario se clasifica en riesgo bajo, medio o alto por dimensión según los rangos de puntaje de la guía ' +
    '(a mayor puntaje, menor riesgo). Los resultados se presentan como número de personas en cada nivel.',
    9, 'normal', 'left', 4, 4
  )
  drawGridTable(helpers, {
    title: 'Puntos de corte por dimensión (puntaje)',
    headers: ['Dimensión', 'Bajo', 'Medio', 'Alto'],
    widths: [88, 24, 24, 24],
    rows: [
      ...PSICOSOCIAL_DIMENSIONS.map(d => [d.label, rangeText(d.cutoffs, 'bajo'), rangeText(d.cutoffs, 'medio'), rangeText(d.cutoffs, 'alto')]),
      ['Global (suma de las 8 dimensiones)', ...NIVELES.map(n => rangeText(PSICOSOCIAL_GLOBAL_CUTOFFS, n))],
    ],
    boldLastRow: true,
  })

  // 4. Participación
  startSection('3. Participación', 36)
  const totalHeadcount = results.reduce((a, r) => a + r.headcount, 0)
  const totalRespondents = results.reduce((a, r) => a + r.respondents, 0)
  drawGridTable(helpers, {
    headers: ['Unidad', 'Trabajadores', 'Respondieron', 'Participación', 'Resultados'],
    widths: [60, 25, 25, 25, 25],
    rows: [
      ...results.map(r => [
        r.unitName,
        String(r.headcount),
        String(r.respondents),
        `${pct(r.respondents, r.headcount)}%`,
        r.suppressed ? 'Suprimida' : 'Publicados',
      ]),
      ['Total', String(totalHeadcount), String(totalRespondents), `${pct(totalRespondents, totalHeadcount)}%`, ''],
    ],
    boldLastRow: true,
  })

  // 5. Resultados globales
  startSection('4. Resultados globales', 40)
  const globalCounts = sumLevelCounts(published.map(r => r.distributions!.global))
  const globalTotal = globalCounts.bajo + globalCounts.medio + globalCounts.alto
  if (globalTotal === 0) {
    txt(`Ninguna unidad alcanzó el mínimo de ${minGroup} respuestas para publicar resultados.`, 9, 'italic', 'left', 4, 4)
  } else {
    txt(
      `Nivel de riesgo global de ${plural(globalTotal, 'persona', 'personas')} en ${plural(published.length, 'unidad', 'unidades')} con resultados` +
      (suppressed.length > 0 ? ` (no incluye ${plural(suppressed.length, 'unidad suprimida', 'unidades suprimidas')}).` : '.'),
      9, 'normal', 'left', 4, 3
    )
    drawGridTable(helpers, {
      headers: ['Nivel de riesgo', 'Personas', '%', 'Distribución'],
      widths: [50, 25, 25, CW - 100],
      rows: [
        ...NIVELES.map(n => [n.charAt(0).toUpperCase() + n.slice(1), String(globalCounts[n]), `${pct(globalCounts[n], globalTotal)}%`, '']),
        ['Total', String(globalTotal), '100%', ''],
      ],
      bars: [
        ...NIVELES.map(n => ({ kind: 'single' as const, level: n, value: globalCounts[n], total: globalTotal })),
        { kind: 'stacked' as const, counts: globalCounts },
      ],
      boldLastRow: true,
    })
  }

  // 6. Resultados por unidad y dimensión
  startSection('5. Resultados por unidad y dimensión', 66)
  results.forEach(r => {
    if (r.suppressed || r.distributions === null) {
      txt(`${r.unitName} — Suprimida: ${r.respondents} de ${r.headcount} respondieron (mínimo ${r.minGroup}).`, 9, 'italic', 'left', 0, 4)
      return
    }
    const dist = r.distributions
    drawLevelTable(
      helpers,
      `${r.unitName} — ${r.respondents} de ${r.headcount} respondieron`,
      'Dimensión',
      PSICOSOCIAL_DIMENSIONS
        .filter(d => dist.dimensions[d.key] !== undefined)
        .map(d => ({ label: d.label, counts: dist.dimensions[d.key] }))
    )
  })

  // 7. Detalle de subdimensiones de "Otros puntos importantes"
  startSection(`6. Detalle de "${otros?.label ?? 'Otros puntos importantes'}"`, 66)
  if (published.length === 0) {
    txt('Sin unidades con resultados publicados.', 9, 'italic', 'left', 4, 4)
  }
  published.forEach(r => {
    const dist = r.distributions!
    drawLevelTable(
      helpers,
      `${r.unitName} — subdimensiones`,
      'Subdimensión',
      subdimensions
        .filter(s => dist.subdimensions[s.key] !== undefined)
        .map(s => ({ label: s.label, counts: dist.subdimensions[s.key] }))
    )
  })
  hline()

  // 8. Nota final
  txt(
    `Criterio de anonimato: los resultados de una unidad solo se publican si al menos ${minGroup} personas respondieron; ` +
    'las unidades con menos respuestas se marcan como suprimidas y no se incluyen en los totales globales. ' +
    'El informe contiene únicamente distribuciones agregadas: no incluye respuestas individuales ni permite identificar a ninguna persona.',
    8, 'italic', 'left', 0, 3
  )
  txt(
    'Cuestionario de evaluación de riesgo psicosocial según la guía del Ministerio del Trabajo del Ecuador (octubre 2018). ' +
    'Los resultados reflejan la percepción de quienes respondieron en el período indicado y no reemplazan el análisis de un ' +
    'profesional en psicología o en seguridad y salud ocupacional para definir las medidas de prevención.',
    8, 'italic', 'left', 0, 6
  )

  // 9. Firmas
  drawSignatureBlocks(helpers)

  skip(2)
  txt(`Documento generado el ${formatEcuadorDateTime(new Date().toISOString())} por StorSafe — storsafe.app`, 8, 'italic', 'center', 0, 0)

  // 10. Segundo pase: encabezado (desde la página 2) y paginación
  const totalPages = doc.getNumberOfPages()
  const hdrTxt = `${companyName}  —  Resultados psicosociales: ${assessment.title}`
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p)

    doc.setFont('helvetica', 'normal')
    doc.setFontSize(8)
    doc.setTextColor(120, 120, 120)
    doc.text(`Página ${p} de ${totalPages}`, PW / 2, PH - 10, { align: 'center' })

    if (p > 1) {
      doc.setFontSize(8)
      doc.setTextColor(150, 150, 150)
      doc.text(fitText(helpers, hdrTxt, CW), PW - M, 12, { align: 'right' })
      doc.setDrawColor(200, 200, 200)
      doc.line(M, 15, PW - M, 15)
    }

    doc.setTextColor(0, 0, 0)
    doc.setDrawColor(0, 0, 0)
  }

  doc.save(buildPsicosocialResultsFilename(assessment.title))
}
